import { lengthNumber, lengthUnit } from './header-modules.js';

/** Boundary box for the open image or video, and the title under it. */
export const MEDIA_FRAME = 'media-frame';
export const MEDIA_TITLE = 'media-title';

/** Inset from each viewport edge. Older themes may only have mx / my. */
export const DEFAULT_MEDIA_MARGIN_X = '4vw';
export const DEFAULT_MEDIA_MARGIN_Y = '8dvh';
/** Each side stays under half the viewport, so the picture always has room. */
export const MEDIA_MARGIN_CAP = 45;

export const MEDIA_MARGIN_UNITS = ['vw', 'dvh', 'px', 'em', 'rem', '%'];

/** A box the viewer has actually edited, rather than the empty module fallback. */
export function mediaChromeActive(box) {
    if (!box) return false;
    if (box.anchor === 'free') return true;
    if (box.size || box.color || box.casing) return true;
    if (box.background || (box.backgroundOpacity != null && box.backgroundOpacity !== '')) return true;
    if (box.tracking != null && box.tracking !== '') return true;
    if (box.w && box.w !== 'auto') return true;
    if (box.h && box.h !== 'auto') return true;
    return false;
}

/**
 * A length that cannot push the frame outside the viewport.
 * Viewport units are capped; other units are capped with min().
 */
export function mediaMarginLength(value, axis, fallback) {
    const capUnit = axis === 'x' ? 'vw' : 'dvh';
    const fb = fallback || (axis === 'x' ? DEFAULT_MEDIA_MARGIN_X : DEFAULT_MEDIA_MARGIN_Y);
    if (value == null || value === '') return fb;
    const raw = String(value).trim();
    const unit = lengthUnit(raw, '');
    const n = lengthNumber(raw);
    if (!unit || !Number.isFinite(n)) return fb;
    const clamped = Math.min(MEDIA_MARGIN_CAP, Math.max(0, n));
    if (unit === 'vw' || unit === 'dvh' || unit === 'vh' || unit === '%') {
        const out = unit === 'vh' ? 'dvh' : unit;
        return `${clamped}${out}`;
    }
    return `min(${Math.max(0, n)}${unit}, ${MEDIA_MARGIN_CAP}${capUnit})`;
}

/** Resolve one side, falling back to the older mx / my pair. */
export function mediaFrameSide(box, side) {
    const axis = side === 'ml' || side === 'mr' ? 'x' : 'y';
    const fallback = axis === 'x' ? DEFAULT_MEDIA_MARGIN_X : DEFAULT_MEDIA_MARGIN_Y;
    const legacy = axis === 'x' ? box?.mx : box?.my;
    const raw = box?.[side] != null && box[side] !== '' ? box[side] : legacy;
    return mediaMarginLength(raw, axis, fallback);
}

/** Left, right, top, and bottom insets for the open picture. */
export function mediaFrameMargins(box) {
    return {
        ml: mediaFrameSide(box, 'ml'),
        mr: mediaFrameSide(box, 'mr'),
        mt: mediaFrameSide(box, 'mt'),
        mb: mediaFrameSide(box, 'mb'),
    };
}

/** The frame fills the viewer inset by those margins, so it cannot leave the screen. */
export function mediaFrameStyle(box) {
    const { ml, mr, mt, mb } = mediaFrameMargins(box);
    return {
        position: 'absolute',
        left: ml,
        right: mr,
        top: mt,
        bottom: mb,
        boxSizing: 'border-box',
    };
}

/** Store margins and drop any free position that could sit outside the viewport. */
export function writeMediaFrameMargins(box, patch = {}) {
    const next = { ...(box || {}) };
    ['ml', 'mr', 'mt', 'mb', 'mx', 'my'].forEach((key) => {
        if (patch[key] != null) next[key] = patch[key];
    });
    // Prefer the four sides once any of them is set.
    if (next.ml != null || next.mr != null || next.mt != null || next.mb != null) {
        delete next.mx;
        delete next.my;
    }
    delete next.anchor;
    delete next.x;
    delete next.y;
    delete next.w;
    delete next.h;
    delete next.fit;
    delete next.lockRatio;
    delete next.attach;
    return next;
}
